import React from "react";
import {
  Box,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Skeleton,
} from "@mui/material";
import { HiPencil, HiTrash, HiEye } from "react-icons/hi";
import type { OrganizationDto } from "@/api/types/dtos";

interface OrganizationRowProps {
  org: OrganizationDto;
  onOrgClick: (org: OrganizationDto) => void;
  onEdit: (org: OrganizationDto) => void;
  onDelete: (org: OrganizationDto) => void;
}

export const OrganizationRow: React.FC<OrganizationRowProps> = ({
  org,
  onOrgClick,
  onEdit,
  onDelete,
}) => (
  <TableRow hover sx={{ cursor: "pointer" }} onClick={() => onOrgClick(org)}>
    <TableCell>
      <Typography variant="body2" fontWeight={500}>
        {org.name || "—"}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography variant="body2" color="text.secondary">
        {org.idNumber || "—"}
      </Typography>
    </TableCell>
    <TableCell>
      <Chip
        label={org.isActive ? "Activo" : "Inactivo"}
        color={org.isActive ? "success" : "default"}
        size="small"
        variant="outlined"
      />
    </TableCell>
    <TableCell align="center">
      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
        <Tooltip title="Ver detalle">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onOrgClick(org);
            }}
            aria-label={`Ver detalle de ${org.name}`}
          >
            <HiEye size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(org);
            }}
            aria-label={`Editar ${org.name}`}
          >
            <HiPencil size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(org);
            }}
            aria-label={`Eliminar ${org.name}`}
          >
            <HiTrash size={16} />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  </TableRow>
);

interface OrganizationTableBodyProps {
  isLoading: boolean;
  organizations: OrganizationDto[];
  debouncedSearch: string;
  skeletonRows: number;
  onOrgClick: (org: OrganizationDto) => void;
  onEdit: (org: OrganizationDto) => void;
  onDelete: (org: OrganizationDto) => void;
  onCreateClick: () => void;
}

const OrganizationTableBody: React.FC<OrganizationTableBodyProps> = ({
  isLoading,
  organizations,
  debouncedSearch,
  skeletonRows,
  onOrgClick,
  onEdit,
  onDelete,
  onCreateClick,
}) => {
  if (isLoading) {
    return (
      <TableBody>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <TableRow key={`skeleton-${i}`}>
            <TableCell>
              <Skeleton />
            </TableCell>
            <TableCell>
              <Skeleton width={120} />
            </TableCell>
            <TableCell>
              <Skeleton width={80} />
            </TableCell>
            <TableCell>
              <Skeleton width={120} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  }

  if (organizations.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {debouncedSearch
                ? "No se encontraron organizaciones con ese criterio de búsqueda"
                : "No hay organizaciones registradas"}
            </Typography>
            {!debouncedSearch && (
              <Typography
                component="button"
                variant="body2"
                onClick={onCreateClick}
                sx={{
                  mt: 1,
                  background: "none",
                  border: "none",
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                  font: "inherit",
                }}
              >
                Crear primera organización
              </Typography>
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {organizations.map((org) => (
        <OrganizationRow
          key={org.id}
          org={org}
          onOrgClick={onOrgClick}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </TableBody>
  );
};

export default OrganizationTableBody;
